import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Shield, Users, Heart, MessageCircle, Lock, ChevronLeft } from "lucide-react";
import Seo from "@/components/Seo";

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 pb-16">
      <Seo 
        title="About AskIncog - Why We Built This"
        description="Learn about AskIncog - Bangalore's anonymous community built for real conversations about work, PGs, relationships, and city life."
      />

      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="mx-auto max-w-4xl px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-slate-400 hover:text-white"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <h1 className="text-lg font-semibold text-white">About AskIncog</h1>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Why We Built <span className="text-indigo-400">AskIncog</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-xl mx-auto">
            A safe space for Bangalore's real questions – no judgment, no names.
          </p>
        </div>

        {/* Story Section */}
        <Card className="border-slate-700/50 bg-slate-800/50 backdrop-blur-sm p-6 mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">The Bangalore Reality</h2>
          <div className="space-y-4 text-slate-300 leading-relaxed">
            <p>
              We live in a city of 13 million people, yet sometimes feel completely alone. 
              Work pressure, PG struggles, relationship questions, career confusion – 
              everyone's dealing with something, but no one talks about it openly.
            </p>
            <p>
              <strong className="text-white">Why?</strong> Because we're afraid. Afraid of being judged. 
              Afraid our question is "stupid." Afraid our colleagues or landlord might find out.
            </p>
            <p>
              AskIncog exists because <strong className="text-indigo-400">real questions deserve real answers</strong>, 
              and sometimes you can only be honest when your name isn't attached.
            </p>
          </div>
        </Card>

        {/* Values Section */}
        <h2 className="text-xl font-semibold text-white mb-6">What We Believe</h2>
        <div className="grid md:grid-cols-2 gap-4 mb-12">
          <Card className="border-slate-700/50 bg-slate-800/30 p-5">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-indigo-500/20 p-2">
                <Lock className="h-5 w-5 text-indigo-400" />
              </div>
              <div>
                <h3 className="font-medium text-white mb-1">True Anonymity</h3>
                <p className="text-sm text-slate-400">
                  Your identity is never revealed. We use pseudonyms and never link questions to real identities.
                </p>
              </div>
            </div>
          </Card>

          <Card className="border-slate-700/50 bg-slate-800/30 p-5">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-emerald-500/20 p-2">
                <Heart className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <h3 className="font-medium text-white mb-1">No Judgment</h3>
                <p className="text-sm text-slate-400">
                  Every question is valid. Whether it's about salary negotiation or relationship advice.
                </p>
              </div>
            </div>
          </Card>

          <Card className="border-slate-700/50 bg-slate-800/30 p-5">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-amber-500/20 p-2">
                <Users className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <h3 className="font-medium text-white mb-1">Local & Relevant</h3>
                <p className="text-sm text-slate-400">
                  Built for Bangalore. Topics that matter here – traffic, PGs, tech jobs, dating scene.
                </p>
              </div>
            </div>
          </Card>

          <Card className="border-slate-700/50 bg-slate-800/30 p-5">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-rose-500/20 p-2">
                <Shield className="h-5 w-5 text-rose-400" />
              </div>
              <div>
                <h3 className="font-medium text-white mb-1">Safe & Moderated</h3>
                <p className="text-sm text-slate-400">
                  Hate speech, harassment, and abuse are not allowed. We actively moderate content.
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Trust & Safety */}
        <Card className="border-slate-700/50 bg-slate-800/50 backdrop-blur-sm p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-6 w-6 text-indigo-400" />
            <h2 className="text-xl font-semibold text-white">Trust & Safety</h2>
          </div>
          <div className="space-y-3 text-slate-300 text-sm">
            <p><strong className="text-white">How we handle anonymity:</strong> When you post a question without logging in, we don't store any identifying information. Logged-in users get a pseudonym (like "MidnightCoder" or "BangaloreDreamer") that's separate from their real identity.</p>
            <p><strong className="text-white">Questions vs Answers:</strong> Questions can be asked anonymously by anyone. Answers require login to maintain accountability and trust in responses.</p>
            <p><strong className="text-white">AI Answers:</strong> Some initial responses are from AskIncog AI — these are clearly marked with an "AI" badge. Human community answers are marked as "Member".</p>
            <p><strong className="text-white">What we don't allow:</strong> Hate speech, harassment, bullying, threats, doxxing, explicit content, or anything illegal. Our AI and human moderators actively review content.</p>
            <p><strong className="text-white">Reporting:</strong> See something wrong? Use the report button on any question or answer. We take reports seriously and act within 24 hours.</p>
          </div>
        </Card>

        {/* Community Guidelines */}
        <Card className="border-slate-700/50 bg-slate-800/50 backdrop-blur-sm p-6 mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Community Guidelines</h2>
          <div className="text-slate-300 text-sm space-y-2">
            <p className="text-indigo-400 font-medium">Ask anonymously. Answer responsibly.</p>
            <ul className="list-disc list-inside space-y-1 text-slate-400">
              <li>Be respectful even when you disagree</li>
              <li>Don't share personal information about others</li>
              <li>No hate speech, slurs, or targeted harassment</li>
              <li>No threats, illegal content, or self-harm encouragement</li>
              <li>Keep it relevant to Bangalore life</li>
              <li>Report content that breaks these rules</li>
            </ul>
          </div>
        </Card>

        {/* Contact Section */}
        <Card className="border-indigo-500/30 bg-indigo-500/10 p-6">
          <div className="flex items-center gap-3 mb-4">
            <MessageCircle className="h-6 w-6 text-indigo-400" />
            <h2 className="text-xl font-semibold text-white">Contact Us</h2>
          </div>
          <p className="text-slate-300 mb-4">
            Have questions, feedback, or want to report an issue? We&apos;re here to help.
          </p>
          <p className="text-xs text-slate-500 mb-4">
            Abuse, hate speech, threats, and personal attacks are removed.
          </p>
          <div className="flex flex-wrap gap-3">
            <a 
              href="mailto:hello@askincog.com" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Email Us
            </a>
            <a 
              href="mailto:report@askincog.com" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Report an Issue
            </a>
          </div>
          
          {/* Social Links Section */}
          <div className="mt-6 pt-4 border-t border-slate-700">
            <p className="text-sm text-slate-400 mb-3">Find AskIncog on social</p>
            <div className="flex flex-wrap gap-3">
              {/* Instagram - Verified */}
              <a 
                href="https://instagram.com/askincog24" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-500 hover:to-pink-400 text-white rounded-lg text-sm font-medium transition-all"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                Instagram
              </a>
              {/* X (Twitter) - Verified */}
              <a 
                href="https://x.com/askincog247" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-black hover:bg-gray-800 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                X
              </a>
              {/* YouTube - Verified channel */}
              <a 
                href="https://www.youtube.com/channel/UCoL-ILD6ESPXHbsurJqxmng" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                YouTube
              </a>
              {/* Facebook - Hidden until exact page URL provided */}
              {/* TODO: Add Facebook button when exact page URL is confirmed */}
              {/* LinkedIn - NOT available, intentionally hidden */}
            </div>
          </div>
          
          <p className="text-xs text-slate-500 mt-4">
            Response time: Usually within 24-48 hours
          </p>
        </Card>

        {/* Footer CTA */}
        <div className="text-center mt-12">
          <p className="text-slate-400 mb-4">Ready to ask your first question?</p>
          <Button
            onClick={() => navigate("/")}
            className="bg-indigo-600 hover:bg-indigo-500 text-white px-8"
          >
            Go to AskIncog
          </Button>
        </div>
      </div>
    </div>
  );
}
